import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useForm, Controller } from 'react-hook-form';
import { CustomButton } from '../components/CustomButton';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { AppModal, ModalType } from '../components/AppModal';
import { theme } from '../constants/theme';
import { useAuth } from '../contexts/AuthContext';
import { alphanumericWithoutSpace, removeSpaces, usernameAtFilterRRegex } from '../utils/helpers';

const logo = require('../assets/logo.png');

interface LoginFormData {
    username: string;
    password: string;
}

const LoginScreen = ({ navigation }: any) => {
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
    }>({ visible: false, type: 'error', title: '', message: '' });

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        defaultValues: {
            username: '',
            password: '',
        },
        mode: 'onBlur',
    });

    const onSubmit = useCallback(async (data: LoginFormData) => {
        setLoading(true);
        try {
            const result = await login(data.username, data.password);

            if (!result.success) {
                setModal({
                    visible: true,
                    type: 'error',
                    title: 'Login Failed',
                    message: result.error || 'Invalid credentials',
                });
            }
        } catch (error) {
            console.error('Login error:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'An unexpected error occurred',
            });
        } finally {
            setLoading(false);
        }
    }, [login]);

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAwareScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >

                    {/* Logo Section */}
                    <View style={styles.logoTopContainer}>
                        <Image
                            source={logo}
                            style={styles.logoImage}
                            resizeMode="contain"
                        />
                    </View>

                    {/* Card Container */}
                    <View style={styles.card}>
                        <Text style={styles.cardTitle}>Login</Text>
                        <Text style={styles.cardSubtitle}>Please login with your information below</Text>

                        {/* Username Input */}
                        <Controller
                            control={control}
                            name="username"
                            rules={{
                                required: 'Username is required',
                            }}
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FloatingLabelInput
                                    label="Username*"
                                    value={value}
                                    onChangeText={(text) => onChange(usernameAtFilterRRegex(text))}
                                    onBlur={onBlur}
                                    error={errors.username?.message}
                                    autoCapitalize="none"
                                    autoCorrect={false}
                                />
                            )}
                        />

                        {/* Password Input */}
                        <Controller
                            control={control}
                            name="password"
                            rules={{
                                required: 'Password is required',
                            }}
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FloatingLabelInput
                                    label="Password*"
                                    value={value}
                                    onChangeText={(text) => onChange(removeSpaces(text))}
                                    onBlur={onBlur}
                                    error={errors.password?.message}
                                    secureTextEntry={!showPassword}
                                    rightIcon={
                                        <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                                            <Icon
                                                name={showPassword ? 'eye-off' : 'eye'}
                                                size={20}
                                                color={theme.colors.textSecondary}
                                            />
                                        </TouchableOpacity>
                                    }
                                />
                            )}
                        />

                        {/* Keep me signed in */}
                        {/* <TouchableOpacity
                            style={styles.checkboxContainer}
                            onPress={() => setKeepSignedIn(!keepSignedIn)}
                            activeOpacity={0.8}
                        >
                            <Icon
                                name={keepSignedIn ? "checkbox-marked" : "checkbox-blank-outline"}
                                size={24}
                                color={theme.colors.textSecondary}
                            />
                            <Text style={styles.checkboxLabel}>Keep me signed in</Text>
                        </TouchableOpacity> */}
                    </View>

                    {/* Button */}
                    <CustomButton
                        title="Log In"
                        onPress={handleSubmit(onSubmit)}
                        loading={loading}
                        style={styles.loginButton}
                    />

                    {/* Footer Info */}
                    <View style={styles.footer}>
                        <TouchableOpacity onPress={() => navigation.navigate('ForgotPassword')}>
                            <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
                        </TouchableOpacity>
                        {/* <View style={styles.infoContainer}>
                            <Icon name="information-outline" size={16} color={theme.colors.textSecondary} style={{ marginTop: 2 }} />
                            <Text style={styles.footerText}>
                                New users are managed by administrators
                            </Text>
                        </View> */}
                    </View>

                </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollContent: {
        flexGrow: 1,
        padding: theme.spacing.lg,
        justifyContent: 'center',
    },
    logoTopContainer: {
        alignItems: 'center',
        // marginBottom: theme.spacing.sm, // User commented this out
        marginBottom: theme.spacing.lg,
    },
    logoImage: {
        width: 200,
        height: 150,
    },
    // logoText removed as per user edit
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
        marginBottom: theme.spacing.xl,
    },
    cardTitle: {
        ...theme.typography.h5,
        color: theme.colors.primary,
        marginBottom: theme.spacing.xs,
    },
    cardSubtitle: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.sm,
        fontSize: 12,
    },
    // Removed old input styles (inputGroup, label, input, etc.)
    checkboxContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: theme.spacing.sm,
        marginBottom: theme.spacing.sm,
    },
    checkboxLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginLeft: theme.spacing.sm,
    },
    loginButton: {
        backgroundColor: theme.colors.primary,
        borderRadius: theme.borderRadius.md,
        paddingVertical: theme.spacing.sm,
    },
    footer: {
        marginTop: theme.spacing.sm,
        alignItems: 'flex-end',
    },
    forgotPasswordText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
        marginBottom: theme.spacing.lg,
    },
    infoContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    footerText: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginLeft: theme.spacing.xs,
    },
});

export default LoginScreen;
